/**
 * patagonia-subcollection-nav
 * v0.11 — Patagonia Bend
 *
 * Custom element wrapping sections/subcollection-navigation.liquid. Provides:
 *   - Scroll active item into view on initial connect, AND on viewport
 *     resize / orientation change. Deferred via double-rAF so layout has
 *     settled before measuring overflow.
 *       - Mobile: behavior='auto', inline='start' (instant, faster paint)
 *       - Desktop: behavior='smooth', inline='start' (smooth animation)
 *   - Arrow Left/Right keyboard focus traversal (a11y; matches the
 *     "tablist" pattern from WAI-ARIA practices)
 *
 * Per project rules: lives in assets/custom-subcollection-nav.js (or merged
 * into your existing assets/custom.js — do not edit assets/theme.js).
 * Extends HTMLElement directly. Namespaced under window.PatagoniaBend.
 *
 * Why a custom element instead of an event listener at page load:
 *   - Section rendering API: the section can be re-rendered without a full
 *     page reload (e.g., from the theme editor). connectedCallback fires
 *     every time, so behavior survives editor re-renders.
 *   - Encapsulation: queries are scoped to `this`, no global selectors.
 *
 * Changelog:
 *   v0.11 — FIX: Mobile Safari was running our scroll-into-view via the
 *          double-rAF defer BEFORE layout reached its final state, so
 *          the math produced wrong-but-plausible values and scrollTo
 *          landed at scrollLeft=0 instead of centering the active item.
 *          Confirmed via Web Inspector: manually calling
 *          _scrollActiveIntoView from the console post-load works
 *          correctly (scrolls to ~190px). The double-rAF was firing
 *          before fonts loaded and final item widths settled.
 *
 *          ADD: post-fonts-ready re-scroll. We await document.fonts.ready
 *          and call _scrollActiveIntoView again. Item widths depend on
 *          font metrics; once fonts are committed, layout is final.
 *
 *          ADD: 250ms fallback re-scroll. Belt-and-suspenders for any
 *          environment where fonts.ready doesn't fire as expected, or
 *          where layout is delayed for other reasons (slow CSS, late-
 *          loading images, etc). Cheap (one extra setTimeout), and the
 *          re-scroll is idempotent — if the active item is already
 *          centered, the math produces adjustment=0 and scrollTo is a
 *          no-op.
 *
 *          The original double-rAF path stays as the FIRST attempt.
 *          On environments where it works (desktop, hard reload on
 *          mobile), the post-fonts and timeout paths see an already-
 *          centered active item and do nothing. On Mobile Safari
 *          cross-page navigation, the double-rAF lands too early, and
 *          one of the later paths catches up.
 *   v0.10 — FIX: Mobile Safari was still not centering the active item
 *          when navigating to a collection from a parent or sibling
 *          collection page (centering DID work on hard reload). Root
 *          cause: Mobile Safari aggressively uses bfcache, which restores
 *          the previous page's scroll positions AFTER our JS has run.
 *          Sequence:
 *            1. JS runs connectedCallback, calls scrollTo, centers active.
 *            2. Mobile Safari fires pageshow with event.persisted=true.
 *            3. Mobile Safari restores any cached scroll positions,
 *               clobbering our centering.
 *
 *          ADD: pageshow listener that re-runs centering when
 *          event.persisted === true (bfcache restoration case). Uses
 *          the same double-rAF defer as the initial path so measurements
 *          happen after layout settles. Progress indicator and edge-fade
 *          state are also refreshed since they depend on scrollLeft.
 *
 *          The listener is bound on window (per the pageshow spec), and
 *          is the only top-level reference (no class instance needed),
 *          so it's safe even if connectedCallback hasn't fired yet on
 *          the new page — it will, and the handler defers reading any
 *          state until then.
 *   v0.9 — FIX: Mobile Safari was not centering the active item on
 *          collection page loads. Root cause: Mobile Safari has a
 *          long-standing quirk where scrollIntoView with block:'nearest'
 *          can no-op the horizontal scroll if the element is already
 *          vertically in view — the browser merges visibility checks
 *          across axes rather than evaluating them independently as
 *          the spec calls for. We were relying on scrollIntoView's
 *          axis-independent behavior to scroll horizontally while
 *          leaving vertical alone, and Mobile Safari silently skipped it.
 *
 *          CHANGE: replaced scrollIntoView with manual scrollLeft math
 *          via getBoundingClientRect + scrollTo. We compute the target
 *          scrollLeft that places the active item's horizontal center
 *          at the list's visible center, and call scrollTo({left, behavior}).
 *          This bypasses the Mobile Safari quirk and gives us full
 *          control. The browser auto-clamps scrollLeft to valid range,
 *          so near-edge active items don't scroll past 0 or maxScroll.
 *
 *          scrollIntoView is kept inside a try-catch as a last-resort
 *          fallback for any browser that doesn't support scrollTo with
 *          options (rare; supported back to Edge 79, Safari 14, all
 *          current Chrome and Firefox).
 *   v0.8 — CHANGE: initial scrollIntoView for the active item now uses
 *          inline: 'center' instead of 'start'. The active item lands
 *          in the middle of the visible region (modulo overflow at the
 *          row ends), so siblings on both sides remain visible. Makes
 *          the "scrollable in both directions" affordance obvious.
 *          Arrow-key navigation still uses inline: 'nearest' (intent
 *          is different — keep focused item visible with minimal motion).
 *          ADD: scroll-position state classes on the nav. _updateProgress
 *          now toggles .ps-subnav--scroll-at-start (scrollLeft <= 0) and
 *          .ps-subnav--scroll-at-end (scrollLeft >= maxScroll - 1).
 *          Used by CSS to suppress the left or right fade when there's
 *          nothing hidden on that side. Tolerance on the end check
 *          (1px) accounts for sub-pixel scroll math.
 *   v0.7 — ADD: --ps-subnav-progress-fill CSS variable, written alongside
 *          the existing --ps-subnav-progress-width and -x. The fill value
 *          drives the "progress bar" mode (Pattern A) — the segment grows
 *          from 0% to 100% as scrollLeft goes from 0 to maxScroll. The
 *          existing -width / -x drive the "viewport segment" mode
 *          (Pattern B). Section CSS chooses which to use based on the
 *          .ps-subnav--mobile-* / .ps-subnav--desktop-* classes set from
 *          theme editor settings. JS writes all three values
 *          unconditionally; no JS-side mode awareness.
 *   v0.6 — ADD: progress indicator (viewport segment). Drives two CSS
 *          custom properties on the host: --ps-subnav-progress-width and
 *          --ps-subnav-progress-x, which control the segment's width and
 *          horizontal translate respectively. JS measures scrollWidth /
 *          clientWidth / scrollLeft on the list element and updates the
 *          variables via rAF-coalesced scroll listener (same throttling
 *          pattern as resize). When no overflow exists, JS removes the
 *          'ps-subnav--has-overflow' class from the host; CSS hides the
 *          segment in that state. Initial update fires after the same
 *          double-rAF defer used for scroll-into-view, so layout has
 *          settled before measurement.
 *   v0.5 — CHANGE: unified initial scroll behavior to 'smooth' on both
 *          mobile and desktop. v0.4 used 'auto' (instant) on mobile to
 *          reduce paint competition, but the abrupt jump felt jarring.
 *          Browser-controlled duration (typically 300-700ms) is good
 *          enough that a custom RAF scroller isn't worth the complexity.
 *          Resize re-scroll still uses 'auto' — smooth animation chasing
 *          a user's drag feels laggy.
 *   v0.4 — FIX: scroll-into-view was sometimes a no-op on initial load
 *          because layout hadn't settled when connectedCallback fired —
 *          the row had no horizontal overflow YET, so scrollIntoView had
 *          nothing to scroll. Fixed with double-rAF defer: first rAF
 *          schedules a callback after current frame's paint; second rAF
 *          (nested) guarantees layout for that first frame has committed.
 *          Cheap (~16ms delay), invisible to users, robust against the
 *          layout-not-ready race.
 *          ADD: resize / orientationchange listener. Re-scrolls the active
 *          item into view when the viewport changes (browser resize on
 *          desktop, rotation on mobile). Throttled via rAF coalescing —
 *          if multiple resize events fire during a drag, only one scroll
 *          fires per frame. Cleaned up in disconnectedCallback.
 *   v0.2 — CHANGE: inline='start' (was 'center'). Active item scrolls to
 *          left edge; siblings to the right are visible — better discovery
 *          pattern and legacy parity.
 *          CHANGE: scrollIntoView behavior is 'auto' on mobile (instant,
 *          faster paint) and 'smooth' on desktop. Arrow-key nav stays
 *          'smooth' on both.
 *   v0.1 — Initial implementation.
 */

(function () {
  'use strict';

  // Namespace per project rule. Idempotent if file is included twice.
  window.PatagoniaBend = window.PatagoniaBend || {};

  class PatagoniaSubcollectionNav extends HTMLElement {
    constructor() {
      super();
      this._onKeydown = this._onKeydown.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._onPageshow = this._onPageshow.bind(this);
      // rAF coalescing token. Truthy while a scroll is already queued for
      // the next frame — additional resize events during that frame don't
      // queue more work.
      this._resizeRafPending = false;
      this._scrollRafPending = false;
    }

    connectedCallback() {
      // The scrollable element is the <ul> (per v0.2 CSS — moved scroll
      // behavior from the host to the list so scrollbar styling works
      // consistently across breakpoints).
      this._list = this.querySelector('.ps-subnav__list');
      if (!this._list) return;

      // The outer <nav class="ps-subnav"> is the styling host for the
      // progress indicator (CSS vars are written here, --has-overflow
      // class is toggled here). Looked up once at connect time.
      this._nav = this.closest('.ps-subnav');

      this._links = Array.from(
        this._list.querySelectorAll('.ps-subnav__link')
      );

      // Initial scroll-into-view. Setting toggles this on/off.
      this._shouldScroll = this.dataset.scrollActive === 'true';
      if (this._shouldScroll) {
        // Pass 1 (v0.4): double-rAF defer. Works on desktop, works on
        // hard reload on mobile. On Mobile Safari cross-page navigation,
        // this can fire before fonts load and layout settles — in which
        // case passes 2 and 3 below pick up the slack.
        this._deferScroll(this._initialBehavior());

        // Pass 2 (v0.11): post-fonts-ready re-scroll. Item widths depend
        // on font metrics; once fonts are committed, layout is final.
        // document.fonts.ready is a promise that resolves immediately if
        // fonts are already loaded (warm cache), or once they finish
        // loading (cold cache). Either way, we re-scroll. The re-scroll
        // is idempotent — if the active item is already centered, the
        // math produces adjustment=0 and scrollTo is a no-op.
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => {
            // Re-check that we're still connected — disconnectedCallback
            // may have fired by now (e.g. user navigated away during
            // font load).
            if (!this.isConnected) return;
            this._scrollActiveIntoView(this._initialBehavior());
            this._updateProgress();
          }).catch(() => {
            // fonts.ready rejection is rare; the fallback timeout below
            // covers this case.
          });
        }

        // Pass 3 (v0.11): 250ms fallback re-scroll. Belt-and-suspenders
        // for environments where fonts.ready doesn't fire as expected,
        // or where layout is delayed for other reasons (slow CSS,
        // late-loading images, etc). 250ms is long enough to outlast
        // most Mobile Safari paint quirks but short enough to feel
        // instant to the user (well below the ~400ms perceptual
        // threshold). Idempotent for the same reason as pass 2.
        this._fallbackScrollTimer = window.setTimeout(() => {
          if (!this.isConnected) return;
          this._scrollActiveIntoView(this._initialBehavior());
          this._updateProgress();
        }, 250);
      }

      // Re-scroll on viewport changes — orientation flip on mobile, window
      // drag on desktop. Both feed through the same handler. The same
      // handler also re-measures progress, so we hook it up regardless of
      // _shouldScroll (the progress indicator is independent of the
      // scroll-active-into-view setting).
      window.addEventListener('resize', this._onResize, { passive: true });
      window.addEventListener('orientationchange', this._onResize, { passive: true });

      // pageshow listener (v0.10) — fires on every navigation, including
      // bfcache restorations. Mobile Safari uses bfcache aggressively and
      // restores cached scroll positions AFTER our initial JS has run,
      // which clobbers our scrollTo. By listening for pageshow with
      // event.persisted=true, we re-center after the restoration has
      // settled. Hard reloads don't fire persisted=true, so the
      // double-trigger doesn't apply on those — only the initial path.
      window.addEventListener('pageshow', this._onPageshow);

      // Progress indicator: listen for native scroll on the list. The list
      // is the scrollable element (per v0.2 CSS), not the host or window.
      this._list.addEventListener('scroll', this._onScroll, { passive: true });

      // Initial progress update — deferred via the same double-rAF as the
      // scroll-into-view so layout has settled before we measure overflow.
      this._deferProgressUpdate();

      // Arrow-key focus traversal.
      this.addEventListener('keydown', this._onKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('keydown', this._onKeydown);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('orientationchange', this._onResize);
      window.removeEventListener('pageshow', this._onPageshow);
      if (this._list) {
        this._list.removeEventListener('scroll', this._onScroll);
      }
      // Cancel the v0.11 fallback re-scroll timer if it hasn't fired yet.
      if (this._fallbackScrollTimer) {
        window.clearTimeout(this._fallbackScrollTimer);
        this._fallbackScrollTimer = null;
      }
    }

    _initialBehavior() {
      // v0.5: unified to 'smooth' on both viewports. Browser-controlled
      // duration is fine; abrupt mobile jump felt jarring.
      return 'smooth';
    }

    _deferScroll(behavior) {
      // Double-rAF: schedule after the next paint, then again to ensure
      // the first frame's layout is fully committed before we measure
      // and scroll.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this._scrollActiveIntoView(behavior);
        });
      });
    }

    _scrollActiveIntoView(behavior) {
      if (!this._list) return;
      const active = this._list.querySelector('[aria-current="page"]');
      if (!active) return;

      // Guard: if there's no horizontal overflow, scrolling is a no-op
      // anyway. Skipping the call avoids odd behavior on browsers that
      // mishandle the axis-independent visibility check.
      if (this._list.scrollWidth <= this._list.clientWidth) return;

      // Manual centering (v0.9). We compute the target scrollLeft using
      // bounding rects, which works reliably across browsers including
      // Mobile Safari (where scrollIntoView's axis-independent behavior
      // is buggy — see changelog).
      //
      // Math: getBoundingClientRect returns viewport-relative coordinates
      // for both the active item and the list container. The active
      // item's horizontal center, relative to the list's left edge, is
      //   (activeRect.left - listRect.left) + (activeRect.width / 2)
      // The list's visible center is just (listRect.width / 2). The
      // difference between those two is how much we need to shift the
      // current scroll position to bring active to center.
      //
      // scrollTo clamps the final scrollLeft to [0, maxScroll], so
      // near-edge active items naturally land as close to center as
      // possible without scrolling past the row bounds.
      try {
        const activeRect = active.getBoundingClientRect();
        const listRect = this._list.getBoundingClientRect();
        const activeCenterFromListLeft =
          (activeRect.left - listRect.left) + (activeRect.width / 2);
        const visibleCenter = listRect.width / 2;
        const adjustment = activeCenterFromListLeft - visibleCenter;
        const targetScrollLeft = this._list.scrollLeft + adjustment;

        this._list.scrollTo({
          left: targetScrollLeft,
          behavior: behavior || 'auto',
        });
      } catch (err) {
        // Last-resort fallback for any browser that doesn't support
        // scrollTo with options. scrollIntoView (no options) is
        // universally supported and at minimum brings the item into
        // view, even if not centered. No user-visible failure.
        try {
          active.scrollIntoView();
        } catch (err2) {
          // Give up silently. Active item just won't be auto-scrolled.
        }
      }
    }

    _onResize() {
      // rAF coalescing — many resize events during a drag collapse to a
      // single scroll per frame. Cheaper than setTimeout-based throttling
      // and naturally syncs with the browser's render cycle.
      if (this._resizeRafPending) return;
      this._resizeRafPending = true;
      window.requestAnimationFrame(() => {
        this._resizeRafPending = false;
        // On resize we always use 'auto' — a smooth animation during a
        // user-initiated drag feels laggy. The active item should track
        // with the viewport, not chase it.
        if (this._shouldScroll) {
          this._scrollActiveIntoView('auto');
        }
        // Resize changes clientWidth → overflow status and segment
        // dimensions both change. Always refresh progress on resize.
        this._updateProgress();
      });
    }

    _onScroll() {
      // rAF coalescing for native scroll. iOS Safari can fire ~60 scroll
      // events per second during a swipe — writing CSS variables on each
      // is wasteful. We collapse to one update per frame.
      if (this._scrollRafPending) return;
      this._scrollRafPending = true;
      window.requestAnimationFrame(() => {
        this._scrollRafPending = false;
        this._updateProgress();
      });
    }

    _onPageshow(event) {
      // Fires on every page load (cached or fresh). We only need to act
      // on bfcache restorations (event.persisted === true) — fresh loads
      // are handled by the initial connectedCallback path.
      //
      // On Mobile Safari, bfcache restores cached scroll positions AFTER
      // our connectedCallback has run, so the initial centering gets
      // clobbered. Re-running it here, after the restoration is committed,
      // gives us the centered state the user expects when navigating from
      // a sibling collection.
      //
      // Same double-rAF defer as the initial path — gives layout time to
      // settle before we measure. Also refreshes the progress indicator
      // and edge-fade state classes since those depend on scrollLeft.
      if (!event.persisted) return;
      if (!this._shouldScroll) return;
      this._deferScroll(this._initialBehavior());
      this._deferProgressUpdate();
    }

    _deferProgressUpdate() {
      // Same double-rAF defer as scroll-into-view: wait for the row's
      // final width before measuring overflow. Without this, on initial
      // page load scrollWidth and clientWidth can both be 0 or both
      // equal (no apparent overflow), and the segment never shows up
      // until the user scrolls.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this._updateProgress();
        });
      });
    }

    _updateProgress() {
      if (!this._list || !this._nav) return;
      const total = this._list.scrollWidth;
      const visible = this._list.clientWidth;
      const max = total - visible;

      // No overflow → hide the segment entirely AND clear the scroll-position
      // state classes (no fades needed when nothing's hidden). Clearing both
      // classes here prevents stale state when a row that previously
      // overflowed shrinks to fit (e.g. a viewport resize that triggers
      // wrapping or a font size change).
      if (max <= 0) {
        this._nav.classList.remove('ps-subnav--has-overflow');
        this._nav.classList.remove('ps-subnav--scroll-at-start');
        this._nav.classList.remove('ps-subnav--scroll-at-end');
        return;
      }
      this._nav.classList.add('ps-subnav--has-overflow');

      // Scroll-position state (v0.8). Used by CSS to suppress the left or
      // right fade when there's nothing hidden on that side.
      //   at-start: scrollLeft <= 0 (no tolerance — can't go negative)
      //   at-end:   scrollLeft >= max - 1 (1px tolerance for sub-pixel
      //             scroll math; some browsers/zoom levels leave scrollLeft
      //             slightly short of max even when "fully" scrolled right)
      const scrollLeft = this._list.scrollLeft;
      if (scrollLeft <= 0) {
        this._nav.classList.add('ps-subnav--scroll-at-start');
      } else {
        this._nav.classList.remove('ps-subnav--scroll-at-start');
      }
      if (scrollLeft >= max - 1) {
        this._nav.classList.add('ps-subnav--scroll-at-end');
      } else {
        this._nav.classList.remove('ps-subnav--scroll-at-end');
      }

      // Viewport-segment mode (Pattern B):
      //   width = visible / total
      //   position = scrollLeft / max × (100 - width)
      const widthPct = (visible / total) * 100;
      const positionPct = (scrollLeft / max) * (100 - widthPct);

      // Fill mode (Pattern A):
      //   fill = scrollLeft / max  (grows from 0% to 100%)
      //   position stays anchored at left (handled by CSS in fill mode)
      const fillPct = (scrollLeft / max) * 100;

      // Write all three values. CSS picks which to consume based on the
      // active mode class on the nav.
      this._nav.style.setProperty('--ps-subnav-progress-width', widthPct + '%');
      this._nav.style.setProperty('--ps-subnav-progress-x', positionPct + '%');
      this._nav.style.setProperty('--ps-subnav-progress-fill', fillPct + '%');
    }

    _onKeydown(event) {
      // Only intercept ArrowLeft / ArrowRight when focus is on a link
      // inside this element. Don't swallow Tab; respect default behavior
      // for everything else.
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      const currentIndex = this._links.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      event.preventDefault();

      let nextIndex;
      if (event.key === 'ArrowRight') {
        nextIndex = (currentIndex + 1) % this._links.length;
      } else {
        nextIndex =
          (currentIndex - 1 + this._links.length) % this._links.length;
      }

      const nextLink = this._links[nextIndex];
      if (nextLink) {
        nextLink.focus();
        // Bring the newly focused link into view if it scrolled off-screen.
        // Smooth on arrow nav at all breakpoints — this is a deliberate
        // user action, not an initial paint event.
        try {
          nextLink.scrollIntoView({
            inline: 'nearest',
            block: 'nearest',
            behavior: 'smooth',
          });
        } catch (err) {
          /* no-op */
        }
      }
    }
  }

  // Expose on namespace for debugging / extension.
  window.PatagoniaBend.SubcollectionNav = PatagoniaSubcollectionNav;

  // Guard against double-registration if this file gets loaded twice.
  if (!customElements.get('patagonia-subcollection-nav')) {
    customElements.define(
      'patagonia-subcollection-nav',
      PatagoniaSubcollectionNav
    );
  }
})();